import { useEffect, useState } from 'react';

/** 入力を URL へ反映するまでの既定の待ち時間（ミリ秒）。 */
export const DEFAULT_DEBOUNCE_MS = 300;

/**
 * テキスト入力を URL クエリと同期させるフック。
 *
 * - 入力中の値はローカルに保持し、一定時間止まってから `commit` を呼ぶ
 * - 戻る / 進むで URL 側の値が変わった場合は入力欄を追従させる
 *
 * @param value URL から復元した現在値
 * @param commit URL へ反映する処理。`useCallback` で安定させて渡すこと
 * @param delayMs デバウンス時間
 */
export function useDebouncedUrlValue(
  value: string,
  commit: (next: string) => void,
  delayMs: number = DEFAULT_DEBOUNCE_MS
): readonly [string, (next: string) => void] {
  const [draft, setDraft] = useState(value);
  const [syncedValue, setSyncedValue] = useState(value);

  // URL 側の値が変わったら入力欄を合わせる（レンダー中の状態調整で追加レンダーを避ける）
  if (syncedValue !== value) {
    setSyncedValue(value);
    setDraft(value);
  }

  useEffect(() => {
    if (draft === value) {
      return;
    }

    const timer = setTimeout(() => commit(draft), delayMs);

    return () => clearTimeout(timer);
  }, [draft, value, delayMs, commit]);

  return [draft, setDraft] as const;
}
